import { useState, useCallback, useEffect } from "react";
import { authFetch } from "../../App";
import { toast } from "sonner";
import {
  Search, Check, X, Plus, Pencil, Tag, Trash2, Database, Loader2,
} from "lucide-react";
import { Button } from "../../components/ui/button";

const API = "/api";
const fmt = (n) => n != null ? n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "\u2014";

export function PriceDatabase({ onClose }) {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", ep: "", einheit: "Stk", category: "", keywords: "", note: "" });

  const load = useCallback(async () => {
    const res = await authFetch(`${API}/gaeb/prices?q=${encodeURIComponent(search)}`);
    if (res.ok) setPrices(await res.json());
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm({ name: "", ep: "", einheit: "Stk", category: "", keywords: "", note: "" }); setEditing(null); };

  const handleSave = async () => {
    if (!form.name.trim() || !form.ep) { toast.error("Name und EP erforderlich"); return; }
    const payload = { ...form, ep: parseFloat(String(form.ep).replace(",", ".")) };
    if (isNaN(payload.ep)) { toast.error("Ungueltiger EP"); return; }

    if (editing) {
      await authFetch(`${API}/gaeb/prices/${editing}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      toast.success("Preis aktualisiert");
    } else {
      await authFetch(`${API}/gaeb/prices`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      toast.success("Preis gespeichert");
    }
    resetForm();
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Preis loeschen?")) return;
    await authFetch(`${API}/gaeb/prices/${id}`, { method: "DELETE" });
    toast.success("Geloescht");
    load();
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ name: p.name, ep: String(p.ep).replace(".", ","), einheit: p.einheit || "Stk", category: p.category || "", keywords: p.keywords || "", note: p.note || "" });
  };

  const inp = "w-full px-3 py-2 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 bg-white";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="price-db-overlay">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="price-db-modal">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-slate-600" />
            <h2 className="text-[14px] font-semibold text-gray-900">Preisdatenbank</h2>
            <span className="text-[10px] text-gray-400">{prices.length} Eintraege</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-md"><X className="w-4 h-4 text-gray-400" /></button>
        </div>

        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/80 shrink-0">
          <div className="grid grid-cols-[1fr_80px_70px_1fr_1fr] gap-2">
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Material/Leistung *" className={inp} data-testid="price-name" />
            <input value={form.ep} onChange={(e) => setForm((f) => ({ ...f, ep: e.target.value }))} placeholder="EP *" className={inp + " text-right"} data-testid="price-ep" />
            <input value={form.einheit} onChange={(e) => setForm((f) => ({ ...f, einheit: e.target.value }))} placeholder="ME" className={inp} />
            <input value={form.keywords} onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))} placeholder="Suchbegriffe (kommagetrennt)" className={inp} data-testid="price-keywords" />
            <div className="flex gap-1.5">
              <input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} placeholder="Kategorie" className={inp + " flex-1"} />
              <Button size="sm" onClick={handleSave} className="gap-1 h-9 bg-slate-800 hover:bg-slate-900 shrink-0" data-testid="price-save-btn">
                {editing ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                {editing ? "Speichern" : "Hinzufuegen"}
              </Button>
              {editing && <button onClick={resetForm} className="p-2 hover:bg-gray-200 rounded-lg"><X className="w-3.5 h-3.5 text-gray-400" /></button>}
            </div>
          </div>
        </div>

        <div className="px-5 py-2 border-b border-gray-100 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Preise suchen..." className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300" data-testid="price-search" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : prices.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">Keine Preise vorhanden</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {prices.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50 group" data-testid={`price-row-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-[13px] font-medium text-gray-800">{p.name}</p>
                      {p.category && <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">{p.category}</span>}
                    </div>
                    {p.keywords && <p className="text-[11px] text-gray-400 truncate"><Tag className="w-3 h-3 inline mr-1" />{p.keywords}</p>}
                  </div>
                  <span className="text-sm font-semibold text-gray-900 tabular-nums">{fmt(p.ep)} EUR</span>
                  <span className="text-xs text-gray-400 w-8">{p.einheit}</span>
                  <span className="text-[10px] text-gray-300">{p.used_count || 0}x</span>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => startEdit(p)} className="p-1 hover:bg-gray-200 rounded"><Pencil className="w-3 h-3 text-gray-400" /></button>
                    <button onClick={() => handleDelete(p.id)} className="p-1 hover:bg-red-100 rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
